export type Language = 'en' | 'es';

export const translations = {
  en: {
    // Common
    loading: 'Loading...',
    save: 'Save',
    cancel: 'Cancel',
    done: 'Done',
    copy: 'Copy',
    copied: 'Copied!',

    // Auth
    login: 'Login',
    signup: 'Sign Up',
    logout: 'Logout',
    email: 'Email',
    password: 'Password',
    name: 'Name',
    familyName: 'Family Name',
    loginTitle: 'Welcome Back!',
    loginSubtitle: 'Sign in to continue your affirmation practice',
    signupTitle: 'Join MindsetFamily',
    signupSubtitle: 'Start your family\'s affirmation journey',
    noAccount: "Don't have an account?",
    haveAccount: 'Already have an account?',

    // Dashboard
    goodMorning: 'Good morning',
    goodAfternoon: 'Good afternoon',
    goodEvening: 'Good evening',
    readyForToday: "Ready for today's affirmation?",
    todaysAffirmation: "Today's Affirmation",
    familyMembers: 'Family Members',
    doneToday: 'Done Today',
    totalRecordings: 'Total Recordings',
    bestStreak: 'Best Streak',
    greatJob: 'Great job! You completed today\'s affirmation!',
    recordPrompt: 'Read it aloud and record yourself saying it with conviction!',
    generatePrompt: "Let's generate your personalized affirmation for today!",
    generateButton: 'Generate My Affirmation',
    generating: 'Generating...',

    // Quick Actions
    browseLibrary: 'Browse Library',
    browseLibraryDesc: 'Explore our collection of affirmations',
    generateNew: 'Generate New',
    generateNewDesc: 'Get a fresh AI-generated affirmation',
    listenBack: 'Listen Back',
    listenBackDesc: 'Hear your past recordings',

    // Recording
    tapToRecord: 'Tap to start recording',
    recording: 'Recording... Tap to stop',
    uploading: 'Uploading your recording...',
    recordingSuccess: 'Great job! Recording saved!',
    recordAgain: 'Record Again',
    speakTip: 'Speak clearly and with conviction! Imagine you truly believe every word.',

    // Generate Modal
    generateAffirmation: 'Generate Affirmation',
    chooseFocusArea: 'Choose a focus area for your personalized affirmation:',
    feelingLucky: "I'm Feeling Lucky",
    randomCategory: 'Random category',
    practicePrompt: 'Practice this affirmation by recording yourself!',
    recordEarnPoints: 'Record & Earn Points',
    generateAnother: 'Generate Another',
    earnedPoints: 'Great job! You earned practice points!',

    // Categories
    confidence: 'Confidence',
    confidenceDesc: 'Self-belief and inner strength',
    resilience: 'Resilience',
    resilienceDesc: 'Bouncing back from challenges',
    growth: 'Growth',
    growthDesc: 'Learning and improvement',
    gratitude: 'Gratitude',
    gratitudeDesc: 'Appreciation and thankfulness',
    calm: 'Calm',
    calmDesc: 'Peace and emotional balance',
    success: 'Success',
    successDesc: 'Achievement and preparation',

    // Library
    affirmationLibrary: 'Affirmation Library',
    librarySubtitle: 'Browse and choose affirmations for your practice',
    yourGenerated: 'Your Generated Affirmations',
    standardAffirmations: 'Standard Affirmations',
    noAffirmations: 'No affirmations found for this category.',
    aiGenerated: 'AI Generated',
    all: 'All',

    // Family
    myFamily: 'My Family',
    manageFamily: 'Manage your family members',
    addMember: 'Add Member',
    addFamilyMember: 'Add Family Member',
    childName: "Member's name",
    ageGroup: 'Age Group',
    adding: 'Adding...',
    parent: 'Parent',
    familyLeaderboard: 'Family Leaderboard',
    completeToAppear: 'Complete some affirmations to appear here!',
    dayStreak: 'day streak',
    generateLoginLink: 'Generate Login Link',
    shareLink: 'Share this link with',
    regenerateLink: 'Regenerate Link',

    // Age Groups
    child58: 'Child (5-8 years)',
    child912: 'Child (9-12 years)',
    teen: 'Teen (13-17 years)',
    adult: 'Adult (18+)',

    // Recordings
    myRecordings: 'My Recordings',
    recordingsSubtitle: 'Listen back to your affirmation practice',
    noRecordings: 'No recordings yet',
    startRecording: 'Start practicing to see your recordings here!',

    // Settings/Language
    settings: 'Settings',
    language: 'Language',
    selectLanguage: 'Select Language',
    english: 'English',
    spanish: 'Spanish',
    languageChanged: 'Language updated!',

    // Magic Login
    loggingIn: 'Logging you in...',
    pleaseWait: 'Please wait a moment',
    welcome: 'Welcome',
    takingToAffirmations: 'Taking you to your affirmations...',
    oops: 'Oops!',
    invalidMagicLink: 'This magic link is invalid or has expired',
    askParent: 'Ask a parent to generate a new magic link for you.',
    goToLogin: 'Go to Login',

    // Footer
    footerTagline: 'Growing stronger together, one affirmation at a time',

    // Landing Page
    landingHero: 'Transform Your Family\'s Mindset',
    landingHeroSubtitle: 'Build confidence, resilience, and positivity in your children through daily affirmation practice.',
    getStarted: 'Get Started Free',
    alreadyHaveAccount: 'Already have an account?',
    signIn: 'Sign in',

    // Features Section
    featuresTitle: 'Why Families Love MindsetFamily',
    featureAffirmationsTitle: 'Daily Affirmations',
    featureAffirmationsDesc: 'AI-powered personalized affirmations tailored to each family member\'s age and needs.',
    featureRecordingTitle: 'Voice Recording',
    featureRecordingDesc: 'Record affirmations aloud to reinforce positive thinking through spoken practice.',
    featureStreaksTitle: 'Streak Tracking',
    featureStreaksDesc: 'Stay motivated with daily streaks and celebrate consistency as a family.',
    featureLeaderboardTitle: 'Family Leaderboard',
    featureLeaderboardDesc: 'Friendly competition keeps everyone engaged and practicing together.',
    featureMagicLinksTitle: 'Easy Child Access',
    featureMagicLinksDesc: 'Magic links let kids login easily without remembering passwords.',
    featureBilingualTitle: 'Bilingual Support',
    featureBilingualDesc: 'Full support for English and Spanish to serve more families.',

    // How It Works
    howItWorksTitle: 'How It Works',
    step1Title: 'Create Your Family',
    step1Desc: 'Sign up and add your family members with their age groups.',
    step2Title: 'Generate Affirmations',
    step2Desc: 'Get personalized AI-generated affirmations or browse our library.',
    step3Title: 'Practice Daily',
    step3Desc: 'Record your affirmations and build a daily habit together.',
    step4Title: 'Track Progress',
    step4Desc: 'Watch your streaks grow and celebrate family achievements.',

    // CTA Section
    ctaTitle: 'Ready to Transform Your Family\'s Mindset?',
    ctaSubtitle: 'Join thousands of families building positive habits together.',
    startJourney: 'Start Your Journey',

    // Navigation
    backToHome: 'Back to Home',

    // Research
    research: 'Research',
    researchSubtitle: 'The science behind positive affirmations',
    researchCta: 'Learn the Science',
    researchCtaDesc: 'Discover the research behind affirmations',

    // Affirmation Actions
    changeAffirmation: 'Get a different affirmation',
    refreshing: 'Refreshing...',

    // Email Verification
    verifyingEmail: 'Verifying your email...',
    emailVerified: 'Email Verified!',
    emailVerifiedSuccess: 'Your email has been verified successfully.',
    verificationFailed: 'Verification Failed',
    invalidVerificationLink: 'This verification link is invalid or has expired.',
    redirectingToLogin: 'Redirecting to login...',
    checkYourEmail: 'Check Your Email',

    // Forgot Password
    forgotPassword: 'Forgot password?',
    forgotPasswordSubtitle: 'Enter your email to receive a password reset link',
    sendResetLink: 'Send Reset Link',
    resetEmailSent: 'If an account exists with this email, you will receive a password reset link.',
    resetEmailFailed: 'Failed to send reset email. Please try again.',
    rememberedPassword: 'Remembered your password?',
    backToLogin: 'Back to Login',

    // Reset Password
    resetPasswordSubtitle: 'Enter your new password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    resetPassword: 'Reset Password',
    passwordsDoNotMatch: 'Passwords do not match',
    passwordTooShort: 'Password must be at least 6 characters',
    invalidResetLink: 'Invalid Reset Link',
    requestNewResetLink: 'This reset link is invalid or has expired.',
    requestNewLink: 'Request New Link',
    passwordReset: 'Password Reset!',
    passwordResetSuccess: 'Your password has been reset successfully.',
    passwordResetFailed: 'Failed to reset password. The link may have expired.',
  },

  es: {
    // Common
    loading: 'Cargando...',
    save: 'Guardar',
    cancel: 'Cancelar',
    done: 'Listo',
    copy: 'Copiar',
    copied: '¡Copiado!',

    // Auth
    login: 'Iniciar Sesión',
    signup: 'Registrarse',
    logout: 'Cerrar Sesión',
    email: 'Correo electrónico',
    password: 'Contraseña',
    name: 'Nombre',
    familyName: 'Nombre de Familia',
    loginTitle: '¡Bienvenido de nuevo!',
    loginSubtitle: 'Inicia sesión para continuar tu práctica de afirmaciones',
    signupTitle: 'Únete a MindsetFamily',
    signupSubtitle: 'Comienza el viaje de afirmaciones de tu familia',
    noAccount: '¿No tienes cuenta?',
    haveAccount: '¿Ya tienes cuenta?',

    // Dashboard
    goodMorning: 'Buenos días',
    goodAfternoon: 'Buenas tardes',
    goodEvening: 'Buenas noches',
    readyForToday: '¿Listo para la afirmación de hoy?',
    todaysAffirmation: 'Afirmación de Hoy',
    familyMembers: 'Miembros de Familia',
    doneToday: 'Hechos Hoy',
    totalRecordings: 'Grabaciones Totales',
    bestStreak: 'Mejor Racha',
    greatJob: '¡Excelente! ¡Completaste la afirmación de hoy!',
    recordPrompt: '¡Léelo en voz alta y grábate diciéndolo con convicción!',
    generatePrompt: '¡Generemos tu afirmación personalizada para hoy!',
    generateButton: 'Generar Mi Afirmación',
    generating: 'Generando...',

    // Quick Actions
    browseLibrary: 'Explorar Biblioteca',
    browseLibraryDesc: 'Explora nuestra colección de afirmaciones',
    generateNew: 'Generar Nueva',
    generateNewDesc: 'Obtén una afirmación generada por IA',
    listenBack: 'Escuchar',
    listenBackDesc: 'Escucha tus grabaciones pasadas',

    // Recording
    tapToRecord: 'Toca para empezar a grabar',
    recording: 'Grabando... Toca para detener',
    uploading: 'Subiendo tu grabación...',
    recordingSuccess: '¡Excelente! ¡Grabación guardada!',
    recordAgain: 'Grabar de Nuevo',
    speakTip: '¡Habla claro y con convicción! Imagina que realmente crees cada palabra.',

    // Generate Modal
    generateAffirmation: 'Generar Afirmación',
    chooseFocusArea: 'Elige un área de enfoque para tu afirmación personalizada:',
    feelingLucky: 'Me Siento con Suerte',
    randomCategory: 'Categoría aleatoria',
    practicePrompt: '¡Practica esta afirmación grabándote!',
    recordEarnPoints: 'Grabar y Ganar Puntos',
    generateAnother: 'Generar Otra',
    earnedPoints: '¡Excelente! ¡Ganaste puntos de práctica!',

    // Categories
    confidence: 'Confianza',
    confidenceDesc: 'Autoconfianza y fortaleza interior',
    resilience: 'Resiliencia',
    resilienceDesc: 'Recuperarse de los desafíos',
    growth: 'Crecimiento',
    growthDesc: 'Aprendizaje y mejora',
    gratitude: 'Gratitud',
    gratitudeDesc: 'Apreciación y agradecimiento',
    calm: 'Calma',
    calmDesc: 'Paz y equilibrio emocional',
    success: 'Éxito',
    successDesc: 'Logro y preparación',

    // Library
    affirmationLibrary: 'Biblioteca de Afirmaciones',
    librarySubtitle: 'Explora y elige afirmaciones para tu práctica',
    yourGenerated: 'Tus Afirmaciones Generadas',
    standardAffirmations: 'Afirmaciones Estándar',
    noAffirmations: 'No se encontraron afirmaciones para esta categoría.',
    aiGenerated: 'Generada por IA',
    all: 'Todas',

    // Family
    myFamily: 'Mi Familia',
    manageFamily: 'Administra los miembros de tu familia',
    addMember: 'Agregar Miembro',
    addFamilyMember: 'Agregar Miembro de Familia',
    childName: 'Nombre del miembro',
    ageGroup: 'Grupo de Edad',
    adding: 'Agregando...',
    parent: 'Padre/Madre',
    familyLeaderboard: 'Tabla de Clasificación Familiar',
    completeToAppear: '¡Completa algunas afirmaciones para aparecer aquí!',
    dayStreak: 'días de racha',
    generateLoginLink: 'Generar Enlace de Acceso',
    shareLink: 'Comparte este enlace con',
    regenerateLink: 'Regenerar Enlace',

    // Age Groups
    child58: 'Niño (5-8 años)',
    child912: 'Niño (9-12 años)',
    teen: 'Adolescente (13-17 años)',
    adult: 'Adulto (18+)',

    // Recordings
    myRecordings: 'Mis Grabaciones',
    recordingsSubtitle: 'Escucha tu práctica de afirmaciones',
    noRecordings: 'Aún no hay grabaciones',
    startRecording: '¡Comienza a practicar para ver tus grabaciones aquí!',

    // Settings/Language
    settings: 'Configuración',
    language: 'Idioma',
    selectLanguage: 'Seleccionar Idioma',
    english: 'Inglés',
    spanish: 'Español',
    languageChanged: '¡Idioma actualizado!',

    // Magic Login
    loggingIn: 'Iniciando sesión...',
    pleaseWait: 'Por favor espera un momento',
    welcome: 'Bienvenido',
    takingToAffirmations: 'Llevándote a tus afirmaciones...',
    oops: '¡Ups!',
    invalidMagicLink: 'Este enlace mágico es inválido o ha expirado',
    askParent: 'Pide a un padre que genere un nuevo enlace mágico.',
    goToLogin: 'Ir a Iniciar Sesión',

    // Footer
    footerTagline: 'Creciendo más fuertes juntos, una afirmación a la vez',

    // Landing Page
    landingHero: 'Transforma la Mentalidad de tu Familia',
    landingHeroSubtitle: 'Desarrolla confianza, resiliencia y positividad en tus hijos a través de la práctica diaria de afirmaciones.',
    getStarted: 'Comenzar Gratis',
    alreadyHaveAccount: '¿Ya tienes una cuenta?',
    signIn: 'Iniciar sesión',

    // Features Section
    featuresTitle: 'Por qué las Familias Aman MindsetFamily',
    featureAffirmationsTitle: 'Afirmaciones Diarias',
    featureAffirmationsDesc: 'Afirmaciones personalizadas con IA adaptadas a la edad y necesidades de cada miembro de la familia.',
    featureRecordingTitle: 'Grabación de Voz',
    featureRecordingDesc: 'Graba afirmaciones en voz alta para reforzar el pensamiento positivo a través de la práctica hablada.',
    featureStreaksTitle: 'Seguimiento de Rachas',
    featureStreaksDesc: 'Mantente motivado con rachas diarias y celebra la constancia en familia.',
    featureLeaderboardTitle: 'Tabla de Clasificación Familiar',
    featureLeaderboardDesc: 'La competencia amistosa mantiene a todos comprometidos y practicando juntos.',
    featureMagicLinksTitle: 'Acceso Fácil para Niños',
    featureMagicLinksDesc: 'Los enlaces mágicos permiten que los niños inicien sesión fácilmente sin recordar contraseñas.',
    featureBilingualTitle: 'Soporte Bilingüe',
    featureBilingualDesc: 'Soporte completo en inglés y español para servir a más familias.',

    // How It Works
    howItWorksTitle: 'Cómo Funciona',
    step1Title: 'Crea tu Familia',
    step1Desc: 'Regístrate y agrega a los miembros de tu familia con sus grupos de edad.',
    step2Title: 'Genera Afirmaciones',
    step2Desc: 'Obtén afirmaciones personalizadas generadas por IA o explora nuestra biblioteca.',
    step3Title: 'Practica Diariamente',
    step3Desc: 'Graba tus afirmaciones y construye un hábito diario juntos.',
    step4Title: 'Sigue tu Progreso',
    step4Desc: 'Observa cómo crecen tus rachas y celebra los logros familiares.',

    // CTA Section
    ctaTitle: '¿Listo para Transformar la Mentalidad de tu Familia?',
    ctaSubtitle: 'Únete a miles de familias que construyen hábitos positivos juntas.',
    startJourney: 'Comienza tu Viaje',

    // Navigation
    backToHome: 'Volver al Inicio',

    // Research
    research: 'Investigación',
    researchSubtitle: 'La ciencia detrás de las afirmaciones positivas',
    researchCta: 'Conoce la Ciencia',
    researchCtaDesc: 'Descubre la investigación detrás de las afirmaciones',

    // Affirmation Actions
    changeAffirmation: 'Obtener otra afirmación',
    refreshing: 'Actualizando...',

    // Email Verification
    verifyingEmail: 'Verificando tu correo...',
    emailVerified: '¡Correo Verificado!',
    emailVerifiedSuccess: 'Tu correo ha sido verificado exitosamente.',
    verificationFailed: 'Verificación Fallida',
    invalidVerificationLink: 'Este enlace de verificación es inválido o ha expirado.',
    redirectingToLogin: 'Redirigiendo al inicio de sesión...',
    checkYourEmail: 'Revisa Tu Correo',

    // Forgot Password
    forgotPassword: '¿Olvidaste tu contraseña?',
    forgotPasswordSubtitle: 'Ingresa tu correo para recibir un enlace de restablecimiento',
    sendResetLink: 'Enviar Enlace',
    resetEmailSent: 'Si existe una cuenta con este correo, recibirás un enlace para restablecer tu contraseña.',
    resetEmailFailed: 'Error al enviar el correo. Por favor intenta de nuevo.',
    rememberedPassword: '¿Recordaste tu contraseña?',
    backToLogin: 'Volver al Inicio de Sesión',

    // Reset Password
    resetPasswordSubtitle: 'Ingresa tu nueva contraseña',
    newPassword: 'Nueva Contraseña',
    confirmPassword: 'Confirmar Contraseña',
    resetPassword: 'Restablecer Contraseña',
    passwordsDoNotMatch: 'Las contraseñas no coinciden',
    passwordTooShort: 'La contraseña debe tener al menos 6 caracteres',
    invalidResetLink: 'Enlace Inválido',
    requestNewResetLink: 'Este enlace de restablecimiento es inválido o ha expirado.',
    requestNewLink: 'Solicitar Nuevo Enlace',
    passwordReset: '¡Contraseña Restablecida!',
    passwordResetSuccess: 'Tu contraseña ha sido restablecida exitosamente.',
    passwordResetFailed: 'Error al restablecer la contraseña. El enlace puede haber expirado.',
  }
};

export type TranslationKey = keyof typeof translations.en;

export function t(key: TranslationKey, language: Language = 'en'): string {
  return translations[language][key] || translations.en[key] || key;
}
